import { z } from "zod";
import { QUALITY_FLAGS } from "../../domain/types";

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const OFFSET_TIMESTAMP_PATTERN = /T.*(?:Z|[+-]\d{2}:\d{2})$/;

export const isoDateSchema = z
  .string()
  .regex(ISO_DATE_PATTERN, "Expected an ISO calendar date (YYYY-MM-DD)")
  .refine((value) => {
    const match = ISO_DATE_PATTERN.exec(value);
    if (!match) return false;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(Date.UTC(year, month - 1, day));

    return (
      parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() === month - 1 &&
      parsed.getUTCDate() === day
    );
  }, "Invalid calendar date");

export const offsetTimestampSchema = z
  .string()
  .refine(
    (value) => OFFSET_TIMESTAMP_PATTERN.test(value) && Number.isFinite(Date.parse(value)),
    "Expected an ISO timestamp with an explicit UTC offset",
  );

export const currencyCodeSchema = z
  .string()
  .regex(/^[A-Z]{3}$/, "Expected a three-letter uppercase currency code");

/** Canonical flags understood by settlement eligibility logic. */
export const settlementQualityFlagSchema = z.enum(QUALITY_FLAGS);

/** Canonical flags understood by same-date FX normalization logic. */
export const FX_QUALITY_FLAGS = ["invalid-fx", "source-warning"] as const;
export const fxQualityFlagSchema = z.enum(FX_QUALITY_FLAGS);

export const monthCodeSchema = z.enum([
  "F",
  "G",
  "H",
  "J",
  "K",
  "M",
  "N",
  "Q",
  "U",
  "V",
  "X",
  "Z",
]);

const DELIVERY_MONTH_BY_CODE: Readonly<Record<z.infer<typeof monthCodeSchema>, number>> = {
  F: 1,
  G: 2,
  H: 3,
  J: 4,
  K: 5,
  M: 6,
  N: 7,
  Q: 8,
  U: 9,
  V: 10,
  X: 11,
  Z: 12,
};

export const metricSchema = z.enum([
  "raw-spread",
  "ratio",
  "log-ratio",
  "beta-adjusted-log-residual",
  "local-z",
  "seasonal-z",
]);

export const quoteUnitSchema = z.enum([
  "cad-per-metric-tonne",
  "eur-per-metric-tonne",
  "usd-per-metric-tonne",
  "us-cents-per-bushel",
  "us-cents-per-pound",
]);

const conversionBase = {
  targetCurrency: currencyCodeSchema,
  targetUnit: z.literal("per-metric-tonne"),
  notes: z.string().min(1).nullable().optional(),
} as const;

const unusedFactorSchema = z.null().optional();
const unusedFxPairSchema = z.null().optional();
const requiredFxPairSchema = z.string().regex(/^[A-Z]{6}$/);

export const commonUnitConversionSchema = z.discriminatedUnion("kind", [
  z.object({
    ...conversionBase,
    kind: z.literal("identity"),
    factor: unusedFactorSchema,
    fxPair: unusedFxPairSchema,
  }).strict(),
  z.object({
    ...conversionBase,
    kind: z.literal("multiply"),
    factor: z.number().positive().finite(),
    fxPair: unusedFxPairSchema,
  }).strict(),
  z.object({
    ...conversionBase,
    kind: z.literal("divide"),
    factor: z.number().positive().finite(),
    fxPair: unusedFxPairSchema,
  }).strict(),
  z.object({
    ...conversionBase,
    kind: z.literal("fx-multiply"),
    factor: unusedFactorSchema,
    fxPair: requiredFxPairSchema,
  }).strict(),
  z.object({
    ...conversionBase,
    kind: z.literal("fx-divide"),
    factor: unusedFactorSchema,
    fxPair: requiredFxPairSchema,
  }).strict(),
  z.object({
    ...conversionBase,
    kind: z.literal("compound"),
    factor: z.number().positive().finite(),
    fxPair: requiredFxPairSchema,
  }).strict(),
]);

export const instrumentSchema = z
  .object({
    id: z.string().min(1),
    exchange: z.string().min(1),
    product: z.string().min(1),
    symbol: z.string().min(1),
    assetClass: z.enum(["oilseed", "vegetable-oil", "fx"]),
    quoteCurrency: currencyCodeSchema,
    quoteUnit: quoteUnitSchema,
    commonUnitConversion: commonUnitConversionSchema,
    contractMultiplier: z.number().positive().finite(),
    tickSize: z.number().positive().finite(),
    settlementTimezone: z.string().min(1),
    settlementConvention: z.string().min(1),
    enabled: z.boolean(),
  })
  .strict()
  .superRefine((value, context) => {
    const expectedCurrency = value.quoteUnit.startsWith("cad-")
      ? "CAD"
      : value.quoteUnit.startsWith("eur-")
        ? "EUR"
        : "USD";
    if (value.quoteCurrency !== expectedCurrency) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["quoteCurrency"],
        message: `${value.quoteUnit} requires ${expectedCurrency} quote currency`,
      });
    }

    const conversion = value.commonUnitConversion;
    const isMetricTonneQuote = value.quoteUnit.endsWith("-per-metric-tonne");
    if (conversion.kind === "identity") {
      if (!isMetricTonneQuote || conversion.targetCurrency !== value.quoteCurrency) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["commonUnitConversion", "kind"],
          message: "Identity conversion requires an already target-currency per-metric-tonne quote",
        });
      }
      return;
    }

    if (conversion.kind === "multiply" || conversion.kind === "divide") {
      if (conversion.targetCurrency !== value.quoteCurrency) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["commonUnitConversion", "targetCurrency"],
          message: "A unit-only conversion cannot change quote currency",
        });
      }
      return;
    }

    const directFxPair = `${value.quoteCurrency}${conversion.targetCurrency}`;
    const inverseFxPair = `${conversion.targetCurrency}${value.quoteCurrency}`;
    if (conversion.targetCurrency === value.quoteCurrency) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["commonUnitConversion", "targetCurrency"],
        message: "An FX conversion cannot target the existing quote currency",
      });
    }
    if (
      (conversion.kind === "fx-multiply" && conversion.fxPair !== directFxPair) ||
      (conversion.kind === "fx-divide" && conversion.fxPair !== inverseFxPair) ||
      (conversion.kind === "compound" &&
        conversion.fxPair !== directFxPair &&
        conversion.fxPair !== inverseFxPair)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["commonUnitConversion", "fxPair"],
        message: "FX pair must consistently connect quoteCurrency and targetCurrency",
      });
    }
    if (
      (conversion.kind === "fx-multiply" || conversion.kind === "fx-divide") &&
      !isMetricTonneQuote
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["commonUnitConversion", "kind"],
        message: "FX-only conversion requires an already per-metric-tonne quote",
      });
    }
  });

export const dataAvailabilitySchema = z
  .object({
    firstDate: isoDateSchema.nullable(),
    lastDate: isoDateSchema.nullable(),
    observationCount: z.number().int().nonnegative(),
  })
  .strict();

export const futuresContractSchema = z
  .object({
    id: z.string().min(1),
    instrumentId: z.string().min(1),
    monthCode: monthCodeSchema,
    deliveryYear: z.number().int().min(1900).max(2200),
    deliveryMonth: z.number().int().min(1).max(12),
    firstNoticeDate: isoDateSchema.nullable(),
    lastTradeDate: isoDateSchema.nullable(),
    expiryDate: isoDateSchema.nullable(),
    cropYear: z.string().min(1).nullable(),
    cropPhase: z.string().min(1).nullable(),
    enabled: z.boolean(),
    dataAvailability: dataAvailabilitySchema,
    liquidityMetadata: z.record(z.string(), z.unknown()),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.monthCode === "U" && value.enabled) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["enabled"],
        message: "The initial product configuration cannot enable a U contract",
      });
    }
    if (value.deliveryMonth !== DELIVERY_MONTH_BY_CODE[value.monthCode]) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["deliveryMonth"],
        message: `${value.monthCode} must use delivery month ${DELIVERY_MONTH_BY_CODE[value.monthCode]}`,
      });
    }
    if (
      value.dataAvailability.firstDate !== null &&
      value.dataAvailability.lastDate !== null &&
      value.dataAvailability.firstDate > value.dataAvailability.lastDate
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dataAvailability", "lastDate"],
        message: "lastDate cannot precede firstDate",
      });
    }
  });

export const normalizationStateSchema = z.enum([
  "raw-only",
  "normalized",
  "normalization-unavailable",
]);

export const dailySettlementSchema = z
  .object({
    contractId: z.string().min(1),
    exchangeBusinessDate: isoDateSchema,
    /**
     * Omitted when the upstream release proves the business date and value but
     * does not preserve the exchange publication timestamp.  An acquisition
     * timestamp must never be substituted for this field.
     */
    settlementTimestamp: offsetTimestampSchema.optional(),
    rawPrice: z.number().positive().finite(),
    normalizedPrice: z.number().positive().finite().nullable(),
    normalizationState: normalizationStateSchema,
    volume: z.number().nonnegative().finite().nullable(),
    openInterest: z.number().nonnegative().finite().nullable(),
    source: z.string().min(1),
    qualityFlags: z.array(z.string().min(1)),
    ingestedAt: offsetTimestampSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.normalizationState === "normalized" && value.normalizedPrice === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["normalizedPrice"],
        message: "normalizedPrice is required when normalizationState is normalized",
      });
    }

    if (
      value.normalizationState !== "normalized" &&
      value.normalizedPrice !== null
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["normalizedPrice"],
        message: "normalizedPrice must be null unless normalizationState is normalized",
      });
    }
  });

export const fxRateSchema = z
  .object({
    pair: z.string().regex(/^[A-Z]{6}$/),
    date: isoDateSchema,
    /** Omitted when the upstream release has no genuine fixing timestamp. */
    timestamp: offsetTimestampSchema.optional(),
    rate: z.number().positive().finite(),
    source: z.string().min(1),
    qualityFlags: z.array(z.string().min(1)),
    ingestedAt: offsetTimestampSchema,
  })
  .strict();

export const pairPresetSchema = z
  .object({
    id: z.string().min(1),
    legAInstrumentId: z.string().min(1),
    legBInstrumentId: z.string().min(1),
    primaryDisplayMetric: metricSchema,
    permittedMetrics: z.array(metricSchema).min(1),
    commonCurrency: currencyCodeSchema,
    mappingRuleVersion: z.string().min(1),
    defaultRollingWindows: z.record(z.string(), z.number().int().positive()),
    directionConvention: z
      .object({
        legAIsCanola: z.boolean(),
        positiveMeaning: z.literal("leg-a-rich"),
        negativeMeaning: z.literal("leg-a-cheap"),
      })
      .strict(),
    decompositionConfig: z.record(z.string(), z.unknown()),
    enabled: z.boolean(),
  })
  .strict()
  .superRefine((value, context) => {
    if (!value.permittedMetrics.includes(value.primaryDisplayMetric)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["permittedMetrics"],
        message: "permittedMetrics must include primaryDisplayMetric",
      });
    }
    if (value.legAInstrumentId === value.legBInstrumentId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["legBInstrumentId"],
        message: "Pair legs must reference different instruments",
      });
    }
  });

export const mappingRuleSchema = z
  .object({
    id: z.string().min(1),
    pairPresetId: z.string().min(1),
    calendarStartMonth: z.number().int().min(1).max(12),
    calendarEndMonth: z.number().int().min(1).max(12),
    legAMonthCode: monthCodeSchema,
    legBMonthCode: monthCodeSchema,
    legAContractYearOffset: z.number().int().min(-1).max(1),
    legBContractYearOffset: z.number().int().min(-1).max(1),
    enabled: z.boolean(),
    version: z.string().min(1),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.enabled && (value.legAMonthCode === "U" || value.legBMonthCode === "U")) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["enabled"],
        message: "The initial product configuration cannot enable a mapping that uses U",
      });
    }
  });

export const analysisConfigSchema = z
  .object({
    id: z.string().min(1),
    window: z.number().int().positive().nullable(),
    signalType: z.enum(["local-z", "seasonal-z", "composite"]),
    alignmentRule: z.string().min(1),
    normalizationRule: z.string().min(1),
    seasonalSmoother: z.string().min(1),
    minimumSample: z.number().int().positive(),
    costAssumptions: z.record(z.string(), z.number().finite().nonnegative()),
    version: z.string().min(1),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.window !== null && value.minimumSample > value.window) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["minimumSample"],
        message: "minimumSample cannot exceed a finite analysis window",
      });
    }
  });

export const qualityStatusSchema = z.enum([
  "ok",
  "partial",
  "low-quality",
  "unavailable",
]);

export const approximateProbabilityBandSchema = z.enum([
  "<0.01",
  "0.01-0.05",
  "0.05-0.10",
  ">=0.10",
]);

export const statisticalTestResultSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("available"),
    statistic: z.number().finite(),
    exactPValue: z.null(),
    approximateProbabilityBand: approximateProbabilityBandSchema,
    criticalValues: z.object({
      "1%": z.number().finite(),
      "5%": z.number().finite(),
      "10%": z.number().finite(),
    }).strict(),
    method: z.string().min(1),
    caveat: z.string().min(1),
    sampleSize: z.number().int().positive(),
  }).strict(),
  z.object({
    status: z.literal("not-available"),
    reason: z.string().min(1),
    method: z.string().min(1).nullable().optional(),
    sampleSize: z.number().int().nonnegative(),
  }).strict(),
]);

export const halfLifeResultSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("available"),
    days: z.number().positive().finite(),
    method: z.string().min(1),
    validityGates: z.array(z.string().min(1)).min(1),
    sampleSize: z.number().int().positive(),
  }).strict(),
  z.object({
    status: z.literal("not-available"),
    reason: z.string().min(1),
    method: z.string().min(1).nullable().optional(),
    sampleSize: z.number().int().nonnegative(),
  }).strict(),
]);

export const analysisSnapshotSchema = z
  .object({
    id: z.string().min(1),
    pairPresetId: z.string().min(1),
    contracts: z
      .object({
        legAContractId: z.string().min(1),
        legBContractId: z.string().min(1),
      })
      .strict(),
    asOfDate: isoDateSchema,
    analysisConfigId: z.string().min(1),
    analysisConfigVersion: z.string().min(1),
    rawSpread: z.number().finite().nullable(),
    localZ: z.number().finite().nullable(),
    seasonalZ: z.number().finite().nullable(),
    returnCorrelation: z.number().min(-1).max(1).finite().nullable(),
    beta: z.number().finite().nullable(),
    adf: statisticalTestResultSchema,
    cointegration: statisticalTestResultSchema,
    halfLife: halfLifeResultSchema,
    sampleSize: z.number().int().nonnegative(),
    qualityStatus: qualityStatusSchema,
    alignmentRule: z.string().min(1),
    inputDataAsOfTimestamp: offsetTimestampSchema,
    modelParametersAsOfTimestamp: offsetTimestampSchema,
    result: z.record(z.string(), z.unknown()),
    generatedAt: offsetTimestampSchema,
  })
  .strict()
  .superRefine((value, context) => {
    const generatedAt = Date.parse(value.generatedAt);
    for (const [field, timestamp] of [
      ["inputDataAsOfTimestamp", value.inputDataAsOfTimestamp],
      ["modelParametersAsOfTimestamp", value.modelParametersAsOfTimestamp],
    ] as const) {
      if (Date.parse(timestamp) > generatedAt) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `${field} cannot be later than generatedAt`,
        });
      }
    }
    if (value.sampleSize === 0 || value.qualityStatus === "unavailable") {
      for (const [field, metric] of [
        ["adf", value.adf],
        ["cointegration", value.cointegration],
        ["halfLife", value.halfLife],
      ] as const) {
        if (metric.status === "available") {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: `${field} must be not-available when the snapshot has no usable sample`,
          });
        }
      }
    }
  });

export const marketDataPayloadSchema = z
  .object({
    instruments: z.array(instrumentSchema),
    contracts: z.array(futuresContractSchema),
    settlements: z.array(dailySettlementSchema),
    fxRates: z.array(fxRateSchema),
    pairPresets: z.array(pairPresetSchema),
    mappingRules: z.array(mappingRuleSchema),
    analysisConfigs: z.array(analysisConfigSchema),
    analysisSnapshots: z.array(analysisSnapshotSchema),
  })
  .strict()
  .superRefine((value, context) => {
    const instrumentById = uniqueIndex(value.instruments, "instruments", context);
    const contractById = uniqueIndex(value.contracts, "contracts", context);
    const pairById = uniqueIndex(value.pairPresets, "pairPresets", context);
    uniqueIndex(value.mappingRules, "mappingRules", context);
    uniqueIndex(value.analysisSnapshots, "analysisSnapshots", context);

    const configByKey = new Map<string, (typeof value.analysisConfigs)[number]>();
    value.analysisConfigs.forEach((config, index) => {
      const key = `${config.id}\u0000${config.version}`;
      if (configByKey.has(key)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["analysisConfigs", index],
          message: `Duplicate analysis config ${config.id}@${config.version}`,
        });
      }
      configByKey.set(key, config);
    });

    value.contracts.forEach((contract, index) => {
      if (!instrumentById.has(contract.instrumentId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["contracts", index, "instrumentId"],
          message: `Unknown instrument ${contract.instrumentId}`,
        });
      }
    });
    value.settlements.forEach((settlement, index) => {
      if (!contractById.has(settlement.contractId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["settlements", index, "contractId"],
          message: `Unknown contract ${settlement.contractId}`,
        });
      }
    });
    value.pairPresets.forEach((pair, index) => {
      if (!instrumentById.has(pair.legAInstrumentId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["pairPresets", index, "legAInstrumentId"],
          message: `Unknown instrument ${pair.legAInstrumentId}`,
        });
      }
      if (!instrumentById.has(pair.legBInstrumentId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["pairPresets", index, "legBInstrumentId"],
          message: `Unknown instrument ${pair.legBInstrumentId}`,
        });
      }
    });

    const occupiedMonths = new Map<string, Map<number, string>>();
    value.mappingRules.forEach((rule, index) => {
      const pair = pairById.get(rule.pairPresetId);
      if (!pair) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["mappingRules", index, "pairPresetId"],
          message: `Unknown pair preset ${rule.pairPresetId}`,
        });
        return;
      }
      if (rule.version !== pair.mappingRuleVersion) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["mappingRules", index, "version"],
          message: `Mapping version must match pair preset version ${pair.mappingRuleVersion}`,
        });
      }
      if (!rule.enabled) return;
      const key = `${rule.pairPresetId}\u0000${rule.version}`;
      const months = occupiedMonths.get(key) ?? new Map<number, string>();
      for (const month of inclusiveCalendarMonths(rule.calendarStartMonth, rule.calendarEndMonth)) {
        const priorRule = months.get(month);
        if (priorRule) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["mappingRules", index],
            message: `Enabled mapping overlaps ${priorRule} in calendar month ${month}`,
          });
        } else {
          months.set(month, rule.id);
        }
      }
      occupiedMonths.set(key, months);
    });

    value.analysisSnapshots.forEach((snapshot, index) => {
      const pair = pairById.get(snapshot.pairPresetId);
      const legA = contractById.get(snapshot.contracts.legAContractId);
      const legB = contractById.get(snapshot.contracts.legBContractId);
      if (!pair) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["analysisSnapshots", index, "pairPresetId"],
          message: `Unknown pair preset ${snapshot.pairPresetId}`,
        });
      }
      if (!legA || !legB) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["analysisSnapshots", index, "contracts"],
          message: "Snapshot references an unknown contract",
        });
      } else if (
        pair &&
        (legA.instrumentId !== pair.legAInstrumentId || legB.instrumentId !== pair.legBInstrumentId)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["analysisSnapshots", index, "contracts"],
          message: "Snapshot contracts do not match the pair preset legs",
        });
      }
      const config = configByKey.get(
        `${snapshot.analysisConfigId}\u0000${snapshot.analysisConfigVersion}`,
      );
      if (!config) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["analysisSnapshots", index, "analysisConfigVersion"],
          message: `Unknown analysis config ${snapshot.analysisConfigId}@${snapshot.analysisConfigVersion}`,
        });
      } else if (snapshot.sampleSize < config.minimumSample) {
        for (const [field, metric] of [
          ["adf", snapshot.adf],
          ["cointegration", snapshot.cointegration],
          ["halfLife", snapshot.halfLife],
        ] as const) {
          if (metric.status === "available") {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["analysisSnapshots", index, field],
              message: `${field} must be not-available below config minimumSample ${config.minimumSample}`,
            });
          }
        }
      }
    });
  });

export const dataCoverageSchema = z
  .object({
    sourceId: z.string().min(1),
    startDate: isoDateSchema.nullable(),
    endDate: isoDateSchema.nullable(),
    lastUpdatedAt: offsetTimestampSchema.nullable(),
    instrumentIds: z.array(z.string().min(1)),
    contractCount: z.number().int().nonnegative(),
    settlementCount: z.number().int().nonnegative(),
    fxRateCount: z.number().int().nonnegative(),
    qualityStatus: qualityStatusSchema,
    provenance: z
      .object({
        revisionModel: z.enum(["point-in-time", "latest-release-only"]),
        historicalSeed: z.boolean(),
        settlementTimestampStatus: z.enum(["available", "unavailable"]),
        fxFixingTimestampStatus: z.enum(["available", "unavailable"]),
        observationCountsStatus: z.enum(["exact", "not-enumerated"]),
        formalWalkForwardEligible: z.boolean(),
        detail: z.string().min(1),
      })
      .strict()
      .optional(),
  })
  .strict();

export function isSyntheticSourceId(value: string): boolean {
  return /(demo|synthetic|fixture)/i.test(value);
}

export const productionSourceSchema = z
  .object({
    id: z.string().min(1).refine((value) => !isSyntheticSourceId(value), {
      message: "Production source cannot identify demo, synthetic, or fixture data",
    }),
    name: z.string().min(1),
    kind: z.enum(["vendor-api", "licensed-file", "database"]),
    license: z.string().min(1).nullable().optional(),
  })
  .strict();

export const demoDataEnvelopeSchema = z
  .object({
    schemaVersion: z.literal(1),
    mode: z.literal("demo"),
    source: z.literal("synthetic-fixture"),
    asOfDate: isoDateSchema,
    generatedAt: offsetTimestampSchema,
    coverage: dataCoverageSchema,
    data: marketDataPayloadSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.coverage.sourceId !== "synthetic-fixture") {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["coverage", "sourceId"],
        message: "Demo coverage must identify synthetic-fixture as its source",
      });
    }
    value.data.settlements.forEach((settlement, index) => {
      if (settlement.source !== "synthetic-fixture") {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["data", "settlements", index, "source"],
          message: "Demo settlements must use synthetic-fixture source",
        });
      }
    });
    value.data.fxRates.forEach((rate, index) => {
      if (rate.source !== "synthetic-fixture") {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["data", "fxRates", index, "source"],
          message: "Demo FX rates must use synthetic-fixture source",
        });
      }
    });
  });

export const productionDataEnvelopeSchema = z
  .object({
    schemaVersion: z.literal(1),
    mode: z.literal("production"),
    source: productionSourceSchema,
    asOfDate: isoDateSchema,
    generatedAt: offsetTimestampSchema,
    coverage: dataCoverageSchema,
    data: marketDataPayloadSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.coverage.sourceId !== value.source.id) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["coverage", "sourceId"],
        message: "Production coverage source must match the envelope source",
      });
    }
    value.data.settlements.forEach((settlement, index) => {
      if (settlement.source !== value.source.id || isSyntheticSourceId(settlement.source)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["data", "settlements", index, "source"],
          message: "Production settlement source must match the production envelope source",
        });
      }
    });
    value.data.fxRates.forEach((rate, index) => {
      if (rate.source !== value.source.id || isSyntheticSourceId(rate.source)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["data", "fxRates", index, "source"],
          message: "Production FX source must match the production envelope source",
        });
      }
    });
  });

export const dataEnvelopeSchema = z.union([
  demoDataEnvelopeSchema,
  productionDataEnvelopeSchema,
]);

export const MAX_QUERY_FILTER_VALUES = 90;

export const listContractsQuerySchema = z
  .object({
    instrumentIds: z.array(z.string().min(1)).max(MAX_QUERY_FILTER_VALUES).optional(),
    deliveryYearFrom: z.number().int().min(1900).max(2200).optional(),
    deliveryYearTo: z.number().int().min(1900).max(2200).optional(),
    enabledOnly: z.boolean().default(true),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.deliveryYearFrom !== undefined &&
      value.deliveryYearTo !== undefined &&
      value.deliveryYearFrom > value.deliveryYearTo
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["deliveryYearTo"],
        message: "deliveryYearTo cannot precede deliveryYearFrom",
      });
    }
  });

const queryPurposeSchema = z.enum(["latest", "walk-forward"]).default("latest");

export const settlementsQuerySchema = z
  .object({
    contractIds: z.array(z.string().min(1)).min(1).max(MAX_QUERY_FILTER_VALUES),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    asOfTimestamp: offsetTimestampSchema.optional(),
    purpose: queryPurposeSchema,
  })
  .strict()
  .superRefine(validateDatedAsOfQuery);

export const fxRatesQuerySchema = z
  .object({
    pairs: z.array(z.string().regex(/^[A-Z]{6}$/)).min(1).max(MAX_QUERY_FILTER_VALUES),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    asOfTimestamp: offsetTimestampSchema.optional(),
    purpose: queryPurposeSchema,
  })
  .strict()
  .superRefine(validateDatedAsOfQuery);

export const analysisSnapshotsQuerySchema = z
  .object({
    pairPresetIds: z.array(z.string().min(1)).max(MAX_QUERY_FILTER_VALUES).optional(),
    asOfDate: isoDateSchema.optional(),
    analysisConfigId: z.string().min(1).optional(),
  })
  .strict();

const productionResponseBase = {
  schemaVersion: z.literal(1),
  mode: z.literal("production"),
  source: productionSourceSchema,
  asOfDate: isoDateSchema,
  generatedAt: offsetTimestampSchema,
} as const;

export function createProductionApiResponseSchema<T extends z.ZodTypeAny>(dataSchema: T) {
  return z
    .object({
      ...productionResponseBase,
      data: dataSchema,
    })
    .strict();
}

function uniqueIndex<T extends { readonly id: string }>(
  values: readonly T[],
  collection: string,
  context: z.RefinementCtx,
): Map<string, T> {
  const index = new Map<string, T>();
  values.forEach((value, position) => {
    if (index.has(value.id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [collection, position, "id"],
        message: `Duplicate ${collection} id ${value.id}`,
      });
    }
    index.set(value.id, value);
  });
  return index;
}

function inclusiveCalendarMonths(start: number, end: number): readonly number[] {
  const months: number[] = [];
  let month = start;
  while (true) {
    months.push(month);
    if (month === end) return months;
    month = month === 12 ? 1 : month + 1;
    if (months.length > 12) return months;
  }
}

function validateDatedAsOfQuery(
  value: {
    readonly startDate: string;
    readonly endDate: string;
    readonly purpose: "latest" | "walk-forward";
    readonly asOfTimestamp?: string;
  },
  context: z.RefinementCtx,
): void {
  if (value.startDate > value.endDate) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["endDate"],
      message: "endDate cannot precede startDate",
    });
  }
  if (value.purpose === "walk-forward" && value.asOfTimestamp === undefined) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["asOfTimestamp"],
      message: "Walk-forward queries require an ingestion as-of timestamp",
    });
  }
}

export type Instrument = z.infer<typeof instrumentSchema>;
export type FuturesContract = z.infer<typeof futuresContractSchema>;
export type DailySettlement = z.infer<typeof dailySettlementSchema>;
export type FxRate = z.infer<typeof fxRateSchema>;
export type PairPreset = z.infer<typeof pairPresetSchema>;
export type MappingRule = z.infer<typeof mappingRuleSchema>;
export type AnalysisConfig = z.infer<typeof analysisConfigSchema>;
export type AnalysisSnapshot = z.infer<typeof analysisSnapshotSchema>;
export type MarketDataPayload = z.infer<typeof marketDataPayloadSchema>;
export type DataCoverage = z.infer<typeof dataCoverageSchema>;
export type ProductionSource = z.infer<typeof productionSourceSchema>;
export type DemoDataEnvelope = z.infer<typeof demoDataEnvelopeSchema>;
export type ProductionDataEnvelope = z.infer<typeof productionDataEnvelopeSchema>;
export type DataEnvelope = z.infer<typeof dataEnvelopeSchema>;
export type ListContractsQuery = z.input<typeof listContractsQuerySchema>;
export type SettlementsQuery = z.input<typeof settlementsQuerySchema>;
export type FxRatesQuery = z.input<typeof fxRatesQuerySchema>;
export type AnalysisSnapshotsQuery = z.input<typeof analysisSnapshotsQuerySchema>;

export function parseDemoDataEnvelope(value: unknown): DemoDataEnvelope {
  return demoDataEnvelopeSchema.parse(value);
}

export function parseProductionDataEnvelope(value: unknown): ProductionDataEnvelope {
  return productionDataEnvelopeSchema.parse(value);
}

export function parseDataEnvelope(value: unknown): DataEnvelope {
  return dataEnvelopeSchema.parse(value);
}
